import React, { type PropsWithChildren, useEffect, useRef } from 'react';
import { sharedDragController } from '../controllers/DragController';
import { ApplicationView as view } from '@aha-app/mvc';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styles from './styles.module.css';
import DraggableItem from '../DraggableItem';
import { type ItemComponentProps } from './types';
import classNames from 'classnames';
import ItemPlacement, {
  type ItemInterface,
} from 'components/framework/models/ItemPlacement';
import type ListDropHandler from '../ListDropHandler';
import { sharedSelectionController } from 'components/SelectionController/SelectionController';
import type SelectableItem from '../SelectableItem';
import { sortArrayWithFallbacks } from 'components/framework/lib/compareUtils';
import type ApplicationModel from 'components/framework/ApplicationModel';
import { type ListLayout } from '../DropHandler';

export type SortableItemProps = {
  // Is this item currently being dragged?
  dragging: boolean;
  // The sortable item's DraggableItem.
  dragItem: DraggableItem<ApplicationModel>;
  // The ListDropHandler for the item (optional). When the drag is started, it will be set as the initial drop handler.
  dropHandler?: ListDropHandler;
  // Whether to hide this item in the list while it's being dragged.
  hideDragPreviews?: boolean;
  // A unique key for the list the item belongs to.
  listKey: string;
  // A component used to render the individual items.
  ItemComponent: React.ComponentType<ItemComponentProps>;
  // These props aren't used currently, but are defined for parity with DroppableItem
  listLayout: ListLayout;
  // Class name to apply styles to the div that wraps each
  //  row in the item list
  draggableClassName?: string;
};

const SortableItem: React.FC<PropsWithChildren<SortableItemProps>> = ({
  dragging,
  dragItem,
  dropHandler = null,
  hideDragPreviews = true,
  listLayout = 'vertical',
  listKey,
  ItemComponent,
  draggableClassName = null,
}) => {
  const ref = useRef(null);

  const [, drag, dragPreview] = useDrag({
    item: dragItem,
    begin: () => {
      // Get draggable items that match this type
      let draggableItems: DraggableItem<ApplicationModel>[] = [];
      const undraggableItems: SelectableItem<ApplicationModel>[] = [];
      if (sharedSelectionController.has(dragItem)) {
        // This item is already in the selection, so we will drag all of the selected items
        const { selectableItems } = sharedSelectionController;
        selectableItems.forEach(selectableItem => {
          const isDraggable =
            selectableItem instanceof DraggableItem &&
            dragItem.canDragWithItem(selectableItem);

          if (isDraggable) {
            draggableItems.push(selectableItem);
          } else {
            undraggableItems.push(selectableItem);
          }

          undraggableItems.forEach(undraggableItem =>
            sharedSelectionController.remove(undraggableItem)
          );
        });

        // Sort the draggable items so they're in a consistent order
        draggableItems = sortArrayWithFallbacks(
          draggableItems,
          draggableItem => [
            // Place items in the same list key first
            +(draggableItem.listKey !== listKey),
            // Then sort by key, then index
            draggableItem.listKey,
            draggableItem.listIndex,
          ]
        );
      } else {
        draggableItems.push(dragItem);
        sharedSelectionController.set(draggableItems);
      }

      // We need to trigger the drag state change _after_ the drag starts, otherwise
      // we hide the dragged card too soon, and the drag is cancelled.
      requestAnimationFrame(() => {
        let dragProps = {};
        if (dropHandler) {
          dragProps = {
            listKey,
            dropHandler,
            placement: ItemPlacement.before(dragItem.item as ItemInterface),
          };
        }

        sharedDragController.actionStartDrag(draggableItems, dragProps);
      });

      return dragItem;
    },
    canDrag: monitor => dragItem.canDrag(),
    end: (draggedItem, monitor) => {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        sharedDragController.actionStopDrag();
      } else {
        sharedDragController.actionCancelDrag();
      }
    },
  });

  // Suppress the default drag preview.
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  drag(ref);

  const hideItem =
    hideDragPreviews &&
    dragging &&
    sharedDragController.isHoveringOverDropHandler;
  return (
    <div
      ref={ref}
      className={classNames({
        [styles.horizontalItem]: listLayout === 'horizontal',
      })}
      hidden={hideItem}
      style={{ display: hideItem ? 'none' : undefined }}
      onClick={event => dragItem.onClick(event)}
      onContextMenu={event => dragItem.onContextMenu(event)}
    >
      <ItemComponent
        dragging={dragging}
        dragItem={dragItem}
        selectableItem={dragItem}
        selected={sharedSelectionController.has(dragItem)}
      />
    </div>
  );
};

export default view(SortableItem);
